import React, { useCallback, useEffect, useState } from 'react';

import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';

import Dialog from '../Dialog/Dialog';
import { ToastCF } from '../Toast/Toast';
import { CheckBox } from '../Inputs/CheckBox/CheckBox';
import { ButtonPrimary } from '../Button/Button';
import { ButtonsForm } from '../../forms/Buttons/ButtonsForm';
import VisualizadorCartao from './VisualizadorCartao';
import FormularioMarcacoes, { TOTAL_QUESTOES } from './FormularioMarcacoes';
import { useStyles } from './Style';

import ProvaService from '../../services/Prova';
import { MODOS_SESSAO } from './PainelSessaoDigitacao';
import { descreverErroDetalhado } from '../../services/API';

export const mapearRespostas = respostas => {
    const base = Array.from({ length: TOTAL_QUESTOES }, () => '');

    if (!Array.isArray(respostas)) return base;

    respostas.forEach(item => {
        if (!item || !item.resposta) return;

        const indice = Number(item.questao) - 1;

        if (indice >= 0 && indice < TOTAL_QUESTOES) base[indice] = item.resposta;
    });

    return base;
};

const DialogMarcacoes = ({ open, idProva, modo = MODOS_SESSAO.DIGITAR, onClose, onRegistered }) => {
    const classes = useStyles();

    const revisao = modo === MODOS_SESSAO.REVISAR;

    const [prova, setProva] = useState(null);
    const [respostas, setRespostas] = useState(() => mapearRespostas(null));
    const [valido, setValido] = useState(false);
    const [mostrarSugestoes, setMostrarSugestoes] = useState(false);
    const [carregando, setCarregando] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState(null);
    const [toast, setToast] = useState({ open: false, message: '', type: 'error' });

    const notificar = useCallback((message, type = 'error') => setToast({ open: true, message, type }), []);

    const carregar = useCallback(async () => {
        setCarregando(true);
        setErro(null);
        setProva(null);
        setRespostas(mapearRespostas(null));
        setValido(false);
        setMostrarSugestoes(false);

        try {
            const resposta = await ProvaService.carregarDigitacao(idProva);

            if (resposta.status === 204 || !resposta.data) {
                setErro('Não foi possível carregar os dados desta prova.');
                return;
            }

            const lidas = mapearRespostas(resposta.data.respostas);

            setProva(resposta.data);
            setMostrarSugestoes(lidas.some(Boolean));
        } catch (error) {
            const descricao = descreverErroDetalhado(error);

            setErro(descricao.message);
            notificar(descricao.message, descricao.type);
        } finally {
            setCarregando(false);
        }
    }, [idProva, notificar]);

    useEffect(() => {
        if (open && idProva) carregar();
    }, [open, idProva, carregar]);

    const handleClose = () => {
        if (enviando) return;

        if (onClose) onClose();
    };

    const handleConfirmar = async () => {
        if (!valido || enviando) return;

        setEnviando(true);

        try {
            const resposta = revisao
                ? await ProvaService.revisarAlternativamente(idProva, { respostas })
                : await ProvaService.digitarAlternativamente(idProva, { respostas });

            if (onRegistered) onRegistered(resposta.data);
        } catch (error) {
            const descricao = descreverErroDetalhado(error);
            notificar(descricao.message, descricao.type);
        } finally {
            setEnviando(false);
        }
    };

    const sugestoes = prova ? mapearRespostas(prova.respostas) : null;
    const temLidas = Boolean(sugestoes) && sugestoes.some(Boolean);

    const acoes = (
        <ButtonsForm
            onClick={handleConfirmar}
            onBack={handleClose}
            idFocus="saveButtonMarcacoes"
            loading={enviando}
            saveDisabled={!valido || carregando || Boolean(erro)}
            name={revisao ? "Registrar revisão" : "Registrar digitação"}
            nameOnBack="Fechar"
        />
    );

    return (
        <>
            <ToastCF
                open={toast.open}
                message={toast.message}
                type={toast.type}
                handleClose={() => setToast(anterior => ({ ...anterior, open: false }))}
            />

            <Dialog
                maxWidth="lg"
                fullWidth={true}
                openDialog={open}
                closeDialog={handleClose}
                title={prova
                    ? `${revisao ? 'Revisar' : 'Digitar'} Marcações - ${prova.codigo}`
                    : (revisao ? 'Revisar Marcações' : 'Digitar Marcações')}
                actions={acoes}
            >
                {carregando && <LinearProgress />}

                {!carregando && erro &&
                    <div className={classes.painelVazio}>
                        <Typography variant="h6" color="error" gutterBottom>Não foi possível abrir a prova</Typography>
                        <Typography variant="body2" color="textSecondary">{erro}</Typography>
                        <div className={classes.painelAcoes}>
                            <ButtonPrimary name="Tentar novamente" onClick={carregar} />
                        </div>
                    </div>
                }

                {!carregando && !erro && prova &&
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <VisualizadorCartao
                                urlImagemCorte={prova.urlImagemCorte}
                                urlImagemCartao={prova.urlImagemCartao}
                                height="60vh"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <div className={classes.formularioBox}>
                                {temLidas &&
                                    <CheckBox
                                        label="Mostrar respostas registradas"
                                        checked={mostrarSugestoes}
                                        onChange={event => setMostrarSugestoes(event.target.checked)}
                                    />
                                }

                                <FormularioMarcacoes
                                    value={respostas}
                                    onChange={setRespostas}
                                    sugestoes={sugestoes}
                                    showSugestoes={mostrarSugestoes}
                                    disabled={enviando}
                                    onValidChange={setValido}
                                    onSubmit={handleConfirmar}
                                />
                            </div>
                        </Grid>
                    </Grid>
                }
            </Dialog>
        </>
    );
};

export default DialogMarcacoes;
